import { ButtonHTMLAttributes, useEffect, useRef, useState } from 'react';

import { Icon } from '@/new/components/icon';
import { Textarea } from '@/new/components/shadcn/textarea';

interface EditableFieldProps {
  value: string;
  onSave: (value: string) => void;
  onFieldClick?: VoidFunction;
}

export function EditableField({ value: initialValue, onSave, onFieldClick }: EditableFieldProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [value, setValue] = useState(initialValue);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const autoResize = (ref: React.RefObject<HTMLTextAreaElement>) => {
    if (ref.current) {
      ref.current.style.height = '28px'; // Reset the height to its initial value
      ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
    }
  };

  const togleEditMode: ButtonHTMLAttributes<HTMLButtonElement>['onClick'] = (e) => {
    e.stopPropagation();
    if (!isEdit) {
      setIsEdit(true);
      setTimeout(() => {
        autoResize(textAreaRef);
      }, 0);
    } else {
      setIsEdit(false);
      onSave(value);
    }
  };

  const handleChangeFieldValue = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    autoResize(textAreaRef);
  };

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  if (isEdit) {
    return (
      <div className="flex w-full flex-row items-center">
        <Textarea
          ref={textAreaRef}
          className="min-h-0 w-full resize-none overflow-hidden rounded-md pl-2"
          onChange={handleChangeFieldValue}
          onClick={(e) => e.stopPropagation()}
          value={value}
        />
        <button className="ml-2 text-sm" onClick={togleEditMode}>
          <Icon className="lucide--check text-gray-700" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <p className={`${onFieldClick && 'cursor-pointer'} whitespace-pre-wrap`} onClick={onFieldClick}>
        {value}
      </p>
      <button className="ml-2 text-sm" onClick={togleEditMode}>
        <Icon className="lucide--square-pen text-gray-700 opacity-60 duration-150 hover:opacity-100" />
      </button>
    </div>
  );
}
